import { ReconciliationSummary as SummaryType } from "./types";
import { formatCents } from "./helpers";

interface Props {
  summary: SummaryType;
  /** Display name of the processor behind this recon: "Stripe", "MX", or "Processor". */
  processorLabel?: string;
}

/* ------------------------------------------------------------------ */
/* Shared small components                                            */
/* ------------------------------------------------------------------ */

/** Single stat cell — matches Product Mix SummaryBar layout */
function Stat({
  label, value, accent, sub,
}: {
  label: string; value: string; accent?: string; sub?: string;
}) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${accent ?? "text-gray-800"}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

/** Section card wrapper — consistent with Product Mix card style */
function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

/** Section header bar */
function CardHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gap row for waterfall + gap analysis                               */
/* ------------------------------------------------------------------ */

function GapRow({
  label, value, description, bold, muted,
}: {
  label: string;
  value: number;
  description?: string;
  bold?: boolean;
  muted?: boolean;
}) {
  const isPositive = value > 0;
  const isZero = value === 0;
  const color = muted
    ? "text-gray-400"
    : isZero
    ? "text-green-600"
    : isPositive
    ? "text-blue-600"
    : "text-orange-600";
  const formatted = isZero
    ? "$0.00"
    : `${isPositive ? "+" : "−"}${formatCents(Math.abs(value))}`;
  return (
    <div className="flex items-start justify-between py-1">
      <div className="flex-1 min-w-0">
        <span className={bold ? "font-semibold text-gray-900" : muted ? "text-gray-400" : "text-gray-700"}>{label}</span>
        {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
      </div>
      <span className={`ml-4 font-mono tabular-nums whitespace-nowrap ${bold ? "font-bold" : "font-medium"} ${color}`}>
        {formatted}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Corrections projection waterfall                                   */
/* ------------------------------------------------------------------ */

function CorrectionsProjection({ summary, processorLabel: proc = "MX" }: Props) {
  const serverGap = summary.gap_cents;

  const refundGap = summary.gap_refund_mismatch_cents ?? 0;
  const refundRonin = summary.refund_mismatch_ronin_cents ?? 0;
  const refundMxNet = refundRonin + refundGap;

  const matchedGap = summary.gap_matched_cents - refundGap;
  const matchedDiffCount = (summary.matched_difference_count ?? 0) - (summary.refund_mismatch_count ?? 0);

  type WaterfallStep = {
    label: string;
    count: number | null;
    gapChange: number;
    detail?: string;
  };

  const steps: WaterfallStep[] = [];

  // Excluded
  const excluded = summary.gap_excluded_cents ?? 0;
  if (excluded !== 0) {
    const excludedParts: string[] = [];
    if (summary.excluded_other_event_count > 0)
      excludedParts.push(`${summary.excluded_other_event_count} refs from other events`);
    if (summary.voided_both_agree_count > 0)
      excludedParts.push(`${summary.voided_both_agree_count} voided (both agree)`);
    steps.push({
      label: "Excluded from this event",
      count: (summary.excluded_other_event_count ?? 0) + (summary.voided_both_agree_count ?? 0),
      gapChange: -excluded,
      detail: excludedParts.length > 0
        ? excludedParts.join(", ") + ` — in ${proc} total but not relevant to this event`
        : "Refs not relevant to this event",
    });
  }

  if ((summary.refund_mismatch_count ?? 0) > 0) {
    steps.push({
      label: "Fix refund mismatches",
      count: summary.refund_mismatch_count ?? 0,
      gapChange: -refundGap,
      detail: `Ronin payment total: ${formatCents(refundRonin)} · ${proc} net (sale − refund): ${formatCents(refundMxNet)} · Difference: ${formatCents(Math.abs(refundGap))}`,
    });
  }

  if (matchedGap !== 0) {
    steps.push({
      label: "Matched amount differences",
      count: matchedDiffCount > 0 ? matchedDiffCount : null,
      gapChange: -matchedGap,
      detail: `Sum of (${proc} net approved − Ronin amount) per record — verify on Matched tab → Differences Only`,
    });
  }

  if ((summary.voided_still_charged_count ?? 0) > 0 && (summary.gap_voided_cents ?? 0) !== 0) {
    steps.push({
      label: "Fix voided still-charged",
      count: summary.voided_still_charged_count ?? 0,
      gapChange: -(summary.gap_voided_cents ?? 0),
      detail: `${proc} has approved charge but Ronin voided — undo the void or process a refund`,
    });
  }

  if (summary.ghost_declined_count > 0 && summary.gap_ghost_declined_cents !== 0) {
    steps.push({
      label: "Fix ghost declined",
      count: summary.ghost_declined_count,
      gapChange: -summary.gap_ghost_declined_cents,
      detail: `Change Ronin payment status to declined — ${proc} says no charge`,
    });
  }

  if (summary.ghost_approved_count > 0 && summary.gap_ghost_approved_cents !== 0) {
    steps.push({
      label: "Fix ghost approved",
      count: summary.ghost_approved_count,
      gapChange: -summary.gap_ghost_approved_cents,
      detail: `Change Ronin payment status to approved — ${proc} confirms charge`,
    });
  }

  if (summary.orphan_mx_count > 0 && summary.gap_orphan_mx_cents !== 0) {
    steps.push({
      label: `Add orphan ${proc} records to Ronin`,
      count: summary.orphan_mx_count,
      gapChange: -summary.gap_orphan_mx_cents,
      detail: `${proc} has records Ronin doesn't — add missing orders`,
    });
  }

  if (summary.orphan_ronin_count > 0 && summary.gap_orphan_ronin_cents !== 0) {
    steps.push({
      label: "Remove orphan Ronin records",
      count: summary.orphan_ronin_count,
      gapChange: -summary.gap_orphan_ronin_cents,
      detail: `Ronin has records ${proc} doesn't — investigate phantom orders`,
    });
  }

  if ((summary.id_mismatched_count ?? 0) > 0 && summary.gap_id_mismatched_cents !== 0) {
    steps.push({
      label: "Fix ID mismatched",
      count: summary.id_mismatched_count ?? 0,
      gapChange: -summary.gap_id_mismatched_cents,
      detail: "Fix reference IDs — matched by amount + last 4",
    });
  }

  // Status-only fixes
  const statusOnly: { label: string; count: number; detail: string }[] = [];
  if (summary.ghost_approved_count > 0 && summary.gap_ghost_approved_cents === 0) {
    statusOnly.push({ label: "Ghost Approved", count: summary.ghost_approved_count, detail: "Change Ronin payment status to approved — amounts already match" });
  }
  if (summary.ghost_declined_count > 0 && summary.gap_ghost_declined_cents === 0) {
    statusOnly.push({ label: "Ghost Declined", count: summary.ghost_declined_count, detail: "Change Ronin payment status to declined — amounts already match" });
  }
  if (summary.orphan_mx_count > 0 && summary.gap_orphan_mx_cents === 0) {
    statusOnly.push({ label: `Orphan ${proc}`, count: summary.orphan_mx_count, detail: `${proc} has records not in Ronin — no approved amount in ${proc} total` });
  }
  if (summary.orphan_ronin_count > 0 && summary.gap_orphan_ronin_cents === 0) {
    statusOnly.push({ label: "Orphan Ronin", count: summary.orphan_ronin_count, detail: `Ronin has records not in ${proc} — investigate` });
  }
  if ((summary.id_mismatched_count ?? 0) > 0 && summary.gap_id_mismatched_cents === 0) {
    statusOnly.push({ label: "ID Mismatched", count: summary.id_mismatched_count ?? 0, detail: "Fix reference IDs — amounts match" });
  }

  const totalGapChange = steps.reduce((s, step) => s + step.gapChange, 0);
  const projectedGap = serverGap + totalGapChange;

  if (steps.length === 0 && statusOnly.length === 0) return null;

  let running = serverGap;

  return (
    <Card className="mb-4">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
        <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">If We Correct Ronin Data</span>
      </div>
      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
        Informational only — no data is changed. Each row shows what fixing that category does to the gap.
      </div>

      {/* Header: starting gap */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <span className="text-sm font-semibold text-gray-700">Raw discrepancy (all {proc} approved − Ronin total)</span>
        <span className="font-mono tabular-nums font-bold text-gray-900">
          {serverGap === 0 ? "$0.00" : `${serverGap > 0 ? "+" : "−"}${formatCents(Math.abs(serverGap))}`}
        </span>
      </div>

      {/* Waterfall steps */}
      <div className="divide-y divide-gray-100">
        {steps.map((step, i) => {
          running += step.gapChange;
          const changeColor = step.gapChange === 0 ? "text-gray-400"
            : (Math.abs(running) < Math.abs(running - step.gapChange)) ? "text-green-600" : "text-orange-600";
          return (
            <div key={i} className="px-4 py-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">{step.label}</span>
                  {step.count != null && <span className="text-xs text-gray-400">({step.count})</span>}
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-mono tabular-nums text-sm font-medium ${changeColor}`}>
                    {step.gapChange > 0 ? "+" : "−"}{formatCents(Math.abs(step.gapChange))}
                  </span>
                  <span className="font-mono tabular-nums text-sm text-gray-500 w-28 text-right">
                    → {running === 0 ? "$0.00" : `${running > 0 ? "+" : "−"}${formatCents(Math.abs(running))}`}
                  </span>
                </div>
              </div>
              {step.detail && <p className="text-xs text-gray-400 mt-0.5">{step.detail}</p>}
            </div>
          );
        })}
      </div>

      {/* Final projected gap */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200">
        <span className="text-sm font-bold text-gray-900">Projected gap after all corrections</span>
        <span className={`font-mono tabular-nums font-bold text-base ${projectedGap === 0 ? "text-green-600" : Math.abs(projectedGap) < 100 ? "text-green-600" : "text-orange-600"}`}>
          {projectedGap === 0 ? "$0.00" : `${projectedGap > 0 ? "+" : "−"}${formatCents(Math.abs(projectedGap))}`}
        </span>
      </div>

      {projectedGap === 0 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-green-50">
          <p className="text-xs text-green-700 font-medium">All discrepancies are accounted for. Correcting Ronin data would fully close the gap.</p>
        </div>
      )}
      {projectedGap !== 0 && Math.abs(projectedGap) < 100 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-green-50">
          <p className="text-xs text-green-700">Remaining amount is negligible (rounding).</p>
        </div>
      )}
      {projectedGap !== 0 && Math.abs(projectedGap) >= 100 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-orange-50">
          <p className="text-xs text-orange-700">Remaining gap needs further investigation.</p>
        </div>
      )}

      {/* Status-only fixes */}
      {statusOnly.length > 0 && (
        <div className="border-t border-gray-200 px-4 py-3">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Status-only fixes (no gap change)</p>
          <div className="divide-y divide-gray-100">
            {statusOnly.map((s, i) => (
              <div key={i} className="py-1.5">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">{s.label}</span>
                    <span className="text-xs text-gray-400">({s.count})</span>
                  </div>
                  <span className="text-xs text-gray-400 tabular-nums">$0.00</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Gap analysis (collapsible)                                         */
/* ------------------------------------------------------------------ */

function GapAnalysis({ summary, processorLabel: proc = "MX" }: Props) {
  const mxTotal = summary.mx_approved_total_cents;
  const cardOnly = summary.ronin_processed_total_cents;
  const merchantProcessed = summary.ronin_merchant_processed_cents ?? cardOnly;
  const overallGap = mxTotal - merchantProcessed;
  const fb = summary.ronin_financial_breakdown;

  const cardOnlyGap = mxTotal - cardOnly;

  const gapItems: { label: string; value: number; desc: string }[] = [];

  const gapRefundMismatch = summary.gap_refund_mismatch_cents ?? 0;
  const gapMatchedOther = summary.gap_matched_cents - gapRefundMismatch;
  const matchedOtherCount = summary.matched_count - (summary.refund_mismatch_count ?? 0);

  if (gapMatchedOther !== 0) {
    gapItems.push({
      label: `Matched (${matchedOtherCount})`,
      value: gapMatchedOther,
      desc: "Per-ref difference between processor approved amount and Ronin payment amount",
    });
  }

  if (gapRefundMismatch !== 0) {
    gapItems.push({
      label: `Refund Mismatches (${summary.refund_mismatch_count ?? 0})`,
      value: gapRefundMismatch,
      desc: "Order refunded but payment still holds original charge — Ronin payment amount not updated",
    });
  }

  if (summary.gap_ghost_declined_cents !== 0) {
    gapItems.push({
      label: `Ghost Declined (${summary.ghost_declined_count})`,
      value: summary.gap_ghost_declined_cents,
      desc: "Processor declined (not in processor total), Ronin payment approved",
    });
  }

  if (summary.gap_ghost_approved_cents !== 0) {
    gapItems.push({
      label: `Ghost Approved (${summary.ghost_approved_count})`,
      value: summary.gap_ghost_approved_cents,
      desc: "Processor approved (in processor total), Ronin payment declined/failed",
    });
  }

  if (summary.gap_orphan_mx_cents !== 0) {
    gapItems.push({
      label: `Orphan ${proc} (${summary.orphan_mx_count})`,
      value: summary.gap_orphan_mx_cents,
      desc: "Approved in processor total, no matching Ronin order",
    });
  }

  if (summary.gap_orphan_ronin_cents !== 0) {
    gapItems.push({
      label: `Orphan Ronin (${summary.orphan_ronin_count})`,
      value: summary.gap_orphan_ronin_cents,
      desc: "In Ronin payments, no matching processor record",
    });
  }

  if (summary.gap_id_mismatched_cents !== 0) {
    gapItems.push({
      label: `ID Mismatched (${summary.id_mismatched_count})`,
      value: summary.gap_id_mismatched_cents,
      desc: "Paired by amount/last-4 — difference between processor approved and Ronin payment per pair",
    });
  }

  if (summary.gap_voided_cents !== 0) {
    gapItems.push({
      label: `Voided (${summary.voided_count})`,
      value: summary.gap_voided_cents,
      desc: "Processor may still have approved charge, Ronin order voided",
    });
  }

  if (summary.gap_excluded_cents !== 0) {
    gapItems.push({
      label: `Excluded — Other Events${summary.excluded_other_event_count ? ` (${summary.excluded_other_event_count})` : ""}`,
      value: summary.gap_excluded_cents,
      desc: "Processor records whose ref matched a Ronin order under a different event, or unparseable refs",
    });
  }

  const reconTotal = gapItems.reduce((s, i) => s + i.value, 0);
  const reconRemainder = cardOnlyGap - reconTotal;

  const finAdjustment = cardOnly - merchantProcessed;

  if (overallGap === 0 && cardOnlyGap === 0) return null;

  return (
    <Card className="mb-4">
      <details className="group">
        <summary className="px-4 py-2 border-b border-gray-200 bg-gray-50 cursor-pointer select-none list-none flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-gray-400 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Where Does the Gap Come From?</span>
        </summary>

        <div className="p-4">
          {/* Layer 1 */}
          <div className="mb-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">1. Transaction-Level Reconciliation</p>
            <div className="space-y-0.5 text-sm pl-3 border-l-2 border-blue-200">
              <GapRow label="Processor Approved Sales" value={mxTotal} bold />
              <GapRow label="Ronin Payment Total" value={-cardOnly} bold
                description="Sum of payments.amount for processed credit card orders" />
              <div className="border-t border-gray-100 pt-1 mt-1">
                <GapRow label="Transaction-Level Gap" value={cardOnlyGap} bold />
              </div>

              {gapItems.length > 0 && (
                <>
                  <div className="pt-1 text-[11px] text-gray-400 uppercase font-semibold tracking-wider">Explained by:</div>
                  {gapItems.map((item, i) => (
                    <GapRow key={i} label={item.label} value={item.value} description={item.desc} />
                  ))}
                  <div className="border-t border-gray-100 pt-1 mt-1">
                    <GapRow label="Subtotal accounted" value={reconTotal} bold />
                    {Math.abs(reconRemainder) > 0 && (
                      <GapRow label="Unaccounted remainder" value={reconRemainder}
                        description="Gap not explained by any category — investigate if non-zero" />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Layer 2 */}
          {fb && (
            <div className="mb-2">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">2. Financial Report Adjustment</p>
              <div className="space-y-0.5 text-sm pl-3 border-l-2 border-purple-200">
                <GapRow label="Ronin Payment Total" value={cardOnly} bold />
                <GapRow label="Ronin Merchant Processed (Financial Report)" value={-merchantProcessed} bold />
                <div className="border-t border-gray-100 pt-1 mt-1">
                  <GapRow label="Adjustment" value={finAdjustment} bold />
                </div>
                <div className="pt-1 text-[11px] text-gray-400 uppercase font-semibold tracking-wider">Financial Report includes:</div>
                <GapRow label="Credit Sales" value={fb.gross_credit_sales} description="Gross credit card sale amounts" />
                <GapRow label="Credit Tips" value={fb.gross_credit_tips} description="Tips on credit card transactions" />
                <GapRow label="RFID Sales" value={fb.gross_rfid_sales} description="Wristband / RFID tap sale amounts" />
                <GapRow label="RFID Tips" value={fb.gross_rfid_tips} description="Tips on RFID transactions" />
                <GapRow label="Digital Surcharge" value={fb.total_digital_surcharge} description="Card processing fee passed to customer" />
                <GapRow label="Discounts" value={-(fb.total_discounts)} description={fb.total_discounts > 0 ? formatCents(fb.total_discounts) + " in discounts applied" : "None"} />
                <GapRow label="Promo Discounts" value={-(fb.total_promo_discounts)} description={fb.total_promo_discounts > 0 ? formatCents(fb.total_promo_discounts) + " in promo discounts applied" : "None"} />
                <GapRow label="Refunds" value={-(fb.total_refunds)} description={fb.total_refunds > 0 ? formatCents(fb.total_refunds) + " refunded" : "None"} />
                <div className="border-t border-gray-100 pt-1 mt-1">
                  <GapRow label="Financial Report Total (Merchant Processed)" value={merchantProcessed} bold />
                </div>
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-500">
                  The Payment Total uses <span className="font-mono">payments.amount</span> (what {proc} actually charged, including tips &amp; surcharges).
                  The Financial Report computes merchant_processed differently using aggregated sales, tips, surcharges, and deductions.
                  The {formatCents(Math.abs(finAdjustment))} difference comes from how these two calculations handle the same underlying transactions.
                </div>
              </div>
            </div>
          )}
        </div>
      </details>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Main exported panel                                                */
/* ------------------------------------------------------------------ */

export function ReconciliationSummaryPanel({ summary, processorLabel = "MX" }: Props) {
  const mxRaw = summary.mx_approved_total_cents;
  const excluded = summary.gap_excluded_cents ?? 0;
  const cardOnly = summary.ronin_processed_total_cents;
  const mxTotal = mxRaw - excluded;
  const gap = mxTotal - cardOnly;

  const gapColor = (v: number) => v === 0 ? "text-green-600" : v > 0 ? "text-blue-600" : "text-orange-600";

  const voidedSubtext =
    summary.voided_still_charged_count > 0
      ? `${summary.voided_still_charged_count} still charged, ${summary.voided_both_agree_count} both agree`
      : `${summary.voided_both_agree_count} both agree`;

  const locationBreakdown = summary.location_breakdown || [];

  // Excluded explanation
  const excludedParts: string[] = [];
  if (summary.excluded_other_event_count > 0)
    excludedParts.push(`${summary.excluded_other_event_count} refs from other events`);
  if (summary.voided_both_agree_count > 0)
    excludedParts.push(`${summary.voided_both_agree_count} voided (both agree)`);
  const excludedLabel = excludedParts.length > 0
    ? `Excludes ${formatCents(Math.abs(excluded))}: ${excludedParts.join(", ")}`
    : null;
  const dupCount = summary.duplicate_mx_count ?? 0;
  const dupApproved = summary.duplicate_mx_approved_cents ?? 0;
  const dupLabel = dupCount > 0
    ? `Deduped ${dupCount} duplicate rows (−${formatCents(dupApproved)} approved) from overlapping CSVs`
    : null;

  return (
    <div className="space-y-4">
      {/* ---- Top equation: MX vs Ronin ---- */}
      <Card>
        <CardHeader>Reconciliation Overview</CardHeader>
        <div className="p-4">
          <div className="flex items-stretch gap-1">
            <div className="flex-1 rounded-lg border bg-gray-50 border-gray-200 p-3">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{processorLabel} Approved (This Event)</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900">{formatCents(mxTotal)}</p>
              {excludedLabel && <p className="text-[10px] text-gray-400">{excludedLabel}</p>}
              {dupLabel && <p className="text-[10px] text-gray-400">{dupLabel}</p>}
              {(summary.excluded_event_breakdown ?? []).length > 0 && (
                <div className="mt-0.5">
                  {(summary.excluded_event_breakdown ?? []).map((evt, i) => (
                    <p key={i} className="text-[10px] text-gray-400">&nbsp;&nbsp;{evt.event_name}: {formatCents(Math.abs(evt.total_cents))}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-center px-1 text-gray-400 font-bold text-lg select-none shrink-0">vs</div>
            <div className="flex-1 rounded-lg border bg-gray-50 border-gray-200 p-3">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Ronin Payment Total</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums text-gray-900">{formatCents(cardOnly)}</p>
              <p className="text-[10px] text-gray-400">payments.amount (incl. tips & surcharges)</p>
            </div>
            <div className="flex items-center justify-center px-1 text-gray-400 font-bold text-lg select-none shrink-0">=</div>
            <div className={`flex-1 rounded-lg border-2 p-3 ${gap === 0 ? "bg-green-50 border-green-300" : "bg-orange-50 border-orange-300"}`}>
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Discrepancy</p>
              <p className={`mt-0.5 text-lg font-bold tabular-nums ${gapColor(gap)}`}>{gap === 0 ? "$0.00" : `${gap > 0 ? "+" : "−"}${formatCents(Math.abs(gap))}`}</p>
              <p className="text-[10px] text-gray-400">{gap < 0 ? "Ronin overcounts" : gap > 0 ? "Ronin undercounts" : "Exact match"}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* ---- Report Summary stats ---- */}
      <Card>
        <CardHeader>Report Summary</CardHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-y sm:divide-y-0 divide-gray-200">
          <Stat
            label="Match Rate"
            value={`${summary.match_rate_percent}%`}
            accent="text-green-700"
            sub={`${summary.exact_match_count} exact of ${summary.matched_count + summary.ghost_declined_count + summary.ghost_approved_count + (summary.id_mismatched_count ?? 0)}`}
          />
          <Stat
            label="Matched"
            value={summary.matched_count.toLocaleString()}
            sub={
              (summary.rescued_outside_date_range_count ?? 0) > 0
                ? `${summary.rescued_outside_date_range_count} outside date range`
                : (summary.id_mismatched_count ?? 0) > 0 ? `+ ${summary.id_mismatched_count} ID mismatched` : undefined
            }
            accent={(summary.rescued_outside_date_range_count ?? 0) > 0 ? "text-purple-600" : undefined}
          />
          <Stat
            label="Ghost Declined"
            value={summary.ghost_declined_count.toLocaleString()}
            accent={summary.ghost_declined_count > 0 ? "text-red-600" : undefined}
            sub={formatCents(summary.ghost_declined_total_cents)}
          />
          <Stat
            label="Ghost Approved"
            value={summary.ghost_approved_count.toLocaleString()}
            accent={summary.ghost_approved_count > 0 ? "text-yellow-600" : undefined}
            sub={formatCents(summary.ghost_approved_total_cents)}
          />
          <Stat
            label="Orphans"
            value={(summary.orphan_mx_count + summary.orphan_ronin_count).toLocaleString()}
            sub={`${summary.orphan_mx_count} Processor / ${summary.orphan_ronin_count} Ronin`}
          />
          <Stat
            label="ID Mismatched"
            value={(summary.id_mismatched_count ?? 0).toLocaleString()}
            sub="counted in Match Rate"
          />
          <Stat
            label="Voided Orders"
            value={summary.voided_count.toLocaleString()}
            accent={summary.voided_still_charged_count > 0 ? "text-red-600" : undefined}
            sub={voidedSubtext}
          />
        </div>
      </Card>

      {/* ---- Waterfall ---- */}
      <CorrectionsProjection summary={summary} processorLabel={processorLabel} />

      {/* ---- Gap analysis (collapsible) ---- */}
      <GapAnalysis summary={summary} processorLabel={processorLabel} />

      {/* ---- Location Breakdown ---- */}
      {locationBreakdown.length > 0 && (
        <Card>
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Breakdown by Location</span>
            <span className="text-xs text-gray-500">{locationBreakdown.length} location{locationBreakdown.length === 1 ? "" : "s"}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-white border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Location</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Matched</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Matched Total</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Ghost Declined</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Ghost Approved</th>
                  <th className="px-4 py-2 text-right text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Voided</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {locationBreakdown.map((loc) => (
                  <tr key={loc.location_name} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{loc.location_name}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{loc.matched_count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-green-700">{formatCents(loc.matched_total_cents)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {loc.ghost_declined_count > 0 ? (
                        <span className="text-red-600 font-medium">{loc.ghost_declined_count} ({formatCents(loc.ghost_declined_total_cents)})</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {loc.ghost_approved_count > 0 ? (
                        <span className="text-yellow-600 font-medium">{loc.ghost_approved_count} ({formatCents(loc.ghost_approved_total_cents)})</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {loc.voided_count > 0 ? (
                        <span className="text-gray-600">{loc.voided_count} ({formatCents(loc.voided_total_cents)})</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                  <td className="px-4 py-2.5 text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                    {locationBreakdown.reduce((s, l) => s + l.matched_count, 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-green-700">
                    {formatCents(locationBreakdown.reduce((s, l) => s + l.matched_total_cents, 0))}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-red-600">
                    {locationBreakdown.reduce((s, l) => s + l.ghost_declined_count, 0)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-yellow-600">
                    {locationBreakdown.reduce((s, l) => s + l.ghost_approved_count, 0)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                    {locationBreakdown.reduce((s, l) => s + l.voided_count, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
